import React, { useState, useId } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Zap,
  ChevronDown,
  ChevronRight,
  FileText,
  Settings,
  Copy,
  Download,
  Maximize2
} from 'lucide-react';

interface ExecutionTask {
  id: string;
  step: number;
  description: string;
  tool?: string;
  status: 'completed' | 'failed' | 'skipped';
  result?: any;
  error?: string;
  execution_time_ms?: number;
}

interface ExecutionResultProps {
  planId: string;
  planDescription: string;
  tasks: ExecutionTask[];
  totalExecutionTime: number;
  completedAt: string;
  overallStatus: 'success' | 'partial' | 'failed';
  actualResult?: any; // Actual AccountsGenie execution result
  statistics?: any; // Execution statistics from AG
}

export const ExecutionResult: React.FC<ExecutionResultProps> = ({
  planId,
  planDescription,
  tasks,
  totalExecutionTime,
  completedAt,
  overallStatus,
  actualResult,
  statistics
}) => {
  const componentId = useId();
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // 🔍 DEBUG: Log exactly what props the component receives
  console.log('🎯 EXECUTIONRESULT COMPONENT RECEIVED PROPS:', {
    planId,
    planDescription,
    actualResult,
    hasFormattedSummary: !!actualResult?.formatted_summary,
    formattedSummaryPreview: actualResult?.formatted_summary?.substring(0, 100),
    actualResultKeys: actualResult ? Object.keys(actualResult) : null,
    overallStatus,
    tasksCount: tasks.length
  });

  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const formatTime = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const getStatusIcon = (status: ExecutionTask['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'skipped':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: ExecutionTask['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'destructive';
      case 'skipped': return 'outline';
    }
  };

  const getOverallStatusBadge = () => {
    switch (overallStatus) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Completed Successfully</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Partially Completed</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Failed</Badge>;
    }
  };

  const copyResult = async (taskId: string, result: any) => {
    try {
      await navigator.clipboard.writeText(
        typeof result === 'string' ? result : JSON.stringify(result, null, 2)
      );
      setCopiedId(taskId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const exportResults = () => {
    const data = {
      planId,
      planDescription,
      tasks: tasks.map(t => ({
        ...t,
        result: t.result
      })),
      totalExecutionTime,
      completedAt,
      overallStatus
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `execution-result-${planId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const failedCount = tasks.filter(t => t.status === 'failed').length;
  const skippedCount = tasks.filter(t => t.status === 'skipped').length;

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Execution Results
            </CardTitle>
            <CardDescription className="mt-1 text-sm">
              {planDescription}
            </CardDescription>
          </div>
          {getOverallStatusBadge()}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 px-4">
        {/* AccountsGenie Result Summary */}
        {(actualResult || planDescription !== `Executed ${tasks.length} tasks successfully`) && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 space-y-2 border border-blue-200 dark:border-blue-800">
            <h3 className="font-medium text-sm text-blue-900 dark:text-blue-100">AccountsGenie Results</h3>
            <div className="text-sm text-blue-800 dark:text-blue-200">
              {/* Check if we have a formatted summary */}
              {actualResult?.formatted_summary ? (
                <div
                  className="prose prose-sm prose-blue dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: actualResult.formatted_summary
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br />')
                  }}
                />
              ) : (
                <div className="whitespace-pre-wrap">{planDescription}</div>
              )}
            </div>
            {statistics && (
              <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>Statistics:</strong>
                  <pre className="mt-1 text-xs">{JSON.stringify(statistics, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Execution Summary */}
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-sm">Execution Details</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={exportResults}
              className="h-7 px-2 text-xs"
            >
              <Download className="h-3 w-3 mr-1" />
              Export
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span>{formatTime(totalExecutionTime)}</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span>{completedCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <XCircle className="h-3 w-3 text-red-500" />
              <span>{failedCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <AlertCircle className="h-3 w-3 text-yellow-500" />
              <span>{skippedCount}</span>
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            {new Date(completedAt).toLocaleString()}
          </div>
        </div>

        {/* Task Results - Only show if we don't have a formatted summary to avoid duplication */}
        {!actualResult?.formatted_summary && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-sm">Task Details</h3>
            </div>
            <ScrollArea className="max-h-64 pr-2">
              <div className="space-y-2">
                {tasks.map((task, index) => (
                <div
                  key={`${componentId}-task-${task.id || index}`}
                  className="flex flex-col gap-1 p-2 rounded border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <button
                      onClick={() => toggleTaskExpansion(task.id)}
                      className="mt-0.5 p-0.5"
                    >
                      {expandedTasks.has(task.id) ?
                        <ChevronDown className="h-3 w-3" /> :
                        <ChevronRight className="h-3 w-3" />
                      }
                    </button>

                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-medium text-xs">
                      {task.step}
                    </div>

                    <div className="flex-1 space-y-1">
                      <div className="flex items-start justify-between">
                        <p className="text-xs font-medium leading-tight">{task.description}</p>
                        <div className="flex items-center gap-1 ml-2">
                          {getStatusIcon(task.status)}
                          <Badge variant={getStatusColor(task.status) as any} className="text-xs px-1 py-0">
                            {task.status}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {task.tool && (
                          <span className="flex items-center gap-1">
                            <Settings className="h-3 w-3" />
                            {task.tool}
                          </span>
                        )}
                        {task.execution_time_ms && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(task.execution_time_ms)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedTasks.has(task.id) && (
                    <div className="ml-9 mt-1 space-y-2">
                      {task.error && (
                        <Alert className="border-red-200 bg-red-50 py-2">
                          <AlertCircle className="h-3 w-3 text-red-600" />
                          <AlertDescription className="text-xs">
                            {task.error}
                          </AlertDescription>
                        </Alert>
                      )}

                      {task.result && (
                        <div className="bg-muted/50 rounded p-2">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium">Result:</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 px-1"
                              onClick={() => copyResult(task.id, task.result)}
                            >
                              {copiedId === task.id ? (
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                          <div className="bg-background rounded p-2 max-h-32 overflow-auto">
                            <pre className="text-xs whitespace-pre-wrap font-mono">
                              {typeof task.result === 'string'
                                ? task.result
                                : JSON.stringify(task.result, null, 2)
                              }
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
        )}

        {/* Status Alert */}
        {overallStatus === 'failed' && (
          <Alert className="border-red-200 bg-red-50">
            <XCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-sm">
              Execution completed with errors. Some tasks failed to complete successfully.
            </AlertDescription>
          </Alert>
        )}

        {overallStatus === 'partial' && (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-sm">
              Execution partially completed. Some tasks were skipped or could not be executed.
            </AlertDescription>
          </Alert>
        )}

        {overallStatus === 'success' && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-sm">
              All tasks completed successfully!
            </AlertDescription>
          </Alert>
        )}
      </CardContent>

      <CardFooter className="flex justify-end pt-3 pb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={exportResults}
        >
          <Download className="h-3 w-3 mr-1" />
          Export Full Results
        </Button>
      </CardFooter>
    </Card>
  );
};
